import { create } from 'zustand'

import type {
  BankAccountInterface,
  ContractInterface,
  DeliveryAddressInterface,
  OrganisationFieldPermissions,
  OrganisationInterface,
  OrganisationShortType,
  ScenarioType,
  SegmentInterface,
  SegmentShortModel,
  StoreInterface,
  UserContextInterface,
  UserInterface,
} from '@/types'

export interface ContactInfoInterface {
  id: string
  organisation: string
  type: 'EMAIL' | 'ADDRESS' | 'PHONE' | 'FAX'
  description: string
}

export interface UserInfoStateInterface {
  username: string
  userId: string
  tokenRoles: string[]
  organisation: OrganisationInterface
  selectedOrganisation: OrganisationInterface
  segments: SegmentInterface[]

  contractsDto: ContractInterface[]
  storesDto: StoreInterface[]
  segmentDto: SegmentInterface | null
  deliveryAddressesDto: DeliveryAddressInterface[]
  bankAccountsDto: BankAccountInterface[]
  contactInfosDto: ContactInfoInterface[]

  userContext: UserContextInterface | null
  currentUser: UserInterface | null
  userOrganisation: OrganisationShortType | null
  currentClient: OrganisationShortType | null
  contextSegment: SegmentShortModel | null
  scenario: ScenarioType | null
  currentSupplierId: string

  organisationPermissions: OrganisationFieldPermissions | null

  isOrgSwitching: boolean

  setUsername: (username: string) => void
  setUserId: (userId: string) => void
  setTokenRoles: (tokenRoles: string[]) => void
  setOrganisation: (organisation: OrganisationInterface) => void
  setSelectedOrganisation: (organisationSelected: OrganisationInterface) => void
  setSegments: (segments: SegmentInterface[]) => void
  setIsOrgSwitching: (val: boolean) => void

  setContractsDto: (contracts: ContractInterface[]) => void
  setStoresDto: (stores: StoreInterface[]) => void
  setSegmentDto: (segment: SegmentInterface | null) => void
  setDeliveryAddressesDto: (addresses: DeliveryAddressInterface[]) => void
  setBankAccountsDto: (accounts: BankAccountInterface[]) => void
  setContactInfosDto: (infos: ContactInfoInterface[]) => void

  setUserContext: (context: UserContextInterface) => void
  setCurrentUser: (user: UserInterface) => void
  setUserOrganisation: (org: OrganisationShortType) => void
  setCurrentClient: (client: OrganisationShortType | null) => void
  setContextSegment: (segment: SegmentShortModel | null) => void
  setScenario: (scenario: ScenarioType) => void

  setOrganisationPermissions: (
    permissions: OrganisationFieldPermissions
  ) => void

  setInitialUserInfoStore: () => void
}

const initialState = {
  username: '',
  userId: '',
  tokenRoles: [],
  organisation: {} as OrganisationInterface,
  selectedOrganisation: {} as OrganisationInterface,
  segments: [],
  contractsDto: [],
  storesDto: [],
  segmentDto: null,
  deliveryAddressesDto: [],
  bankAccountsDto: [],
  contactInfosDto: [],

  isOrgSwitching: false,

  userContext: null,
  currentUser: null,
  userOrganisation: null,
  currentClient: null,
  contextSegment: null,
  scenario: null,
  currentSupplierId: '',

  organisationPermissions: null,
}

export const useUserInfoStore = create<UserInfoStateInterface>((set) => ({
  ...initialState,
  setIsOrgSwitching: (isOrgSwitching) => set({ isOrgSwitching }),
  setUsername: (username) => set({ username }),
  setUserId: (userId) => set({ userId }),
  setTokenRoles: (tokenRoles) => set({ tokenRoles }),
  setOrganisation: (organisation) => set({ organisation }),
  setSelectedOrganisation: (selectedOrganisation) =>
    set({ selectedOrganisation }),
  setSegments: (segments) => set({ segments }),

  setContractsDto: (contractsDto) => set({ contractsDto }),
  setStoresDto: (storesDto) => set({ storesDto }),
  setSegmentDto: (segmentDto) => set({ segmentDto }),
  setDeliveryAddressesDto: (deliveryAddressesDto) =>
    set({ deliveryAddressesDto }),
  setBankAccountsDto: (bankAccountsDto) => set({ bankAccountsDto }),
  setContactInfosDto: (contactInfosDto) => set({ contactInfosDto }),

  setUserContext: (userContext) =>
    set({
      userContext,
      currentUser: userContext.currentUser,
      userOrganisation: userContext.userOrganisation,
      currentClient: userContext.currentClient,
      contextSegment: userContext.contextSegment,
      scenario: userContext.scenario,
      currentSupplierId: userContext.currentSupplierId,
    }),
  setCurrentUser: (currentUser) => set({ currentUser }),
  setUserOrganisation: (userOrganisation) => set({ userOrganisation }),
  setCurrentClient: (currentClient) => set({ currentClient }),
  setContextSegment: (contextSegment) => set({ contextSegment }),
  setScenario: (scenario) => set({ scenario }),

  setOrganisationPermissions: (organisationPermissions) =>
    set({ organisationPermissions }),

  setInitialUserInfoStore: () => set({ ...initialState }),
}))
